import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { getPreferences, updatePreferences } from '../services/preferencesService';
import { setUserAdminStatus, setUserReviewerStatus, listUserRoles } from '@/services/adminService';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import {
  DEVELOPMENT_MANDATE_SECTIONS,
  EMPTY_DEVELOPMENT_MANDATE,
  normalizeMandate,
  type DevelopmentMandateSelections,
} from '@/config/developmentMandate';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';

const UserPreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [reassessedScripts, setReassessedScripts] = useState<number | null>(null);
  const { isAdmin } = useIsAdmin();
  const [adminEmail, setAdminEmail] = useState('');
  const [grantAdmin, setGrantAdmin] = useState(true);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminNotice, setAdminNotice] = useState<string | null>(null);
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [grantReviewer, setGrantReviewer] = useState(true);
  const [reviewerSaving, setReviewerSaving] = useState(false);
  const [reviewerNotice, setReviewerNotice] = useState<string | null>(null);
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [reviewerEmails, setReviewerEmails] = useState<string[]>([]);
  const [mandate, setMandate] = useState<DevelopmentMandateSelections>({
    ...EMPTY_DEVELOPMENT_MANDATE,
  });

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setLoading(true);
        setError(null);
        const prefs = await getPreferences();
        setMandate(normalizeMandate(prefs.development_mandate));
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load preferences';
        console.error('Error loading preferences:', err);
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void loadPreferences();
  }, []);

  const refreshRoles = async () => {
    try {
      const roles = await listUserRoles();
      setAdminEmails(roles.admins);
      setReviewerEmails(roles.reviewers);
    } catch {
      /* keep existing lists */
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    void refreshRoles();
  }, [isAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    setReassessedScripts(null);

    try {
      const { reassessed_scripts } = await updatePreferences({ development_mandate: mandate });
      setSuccess(true);
      setReassessedScripts(reassessed_scripts);
      setTimeout(() => {
        setSuccess(false);
        setReassessedScripts(null);
      }, 5000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save preferences';
      console.error('Error saving preferences:', err);
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const toggleOption = (sectionKey: keyof DevelopmentMandateSelections, value: string) => {
    setMandate((prev) => {
      const current = prev[sectionKey];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [sectionKey]: next };
    });
  };

  if (loading) {
    return (
      <IndexWorkspaceShell activeNav="settings">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-index-accent border-t-transparent" />
        </div>
      </IndexWorkspaceShell>
    );
  }

  return (
    <IndexWorkspaceShell activeNav="settings">
      <div className="mx-auto max-w-5xl space-y-8">
        <header>
          <p className="ni-label mb-2">Settings</p>
          <h1 className="text-2xl md:text-3xl font-sans font-bold text-white">
            Account mandate defaults
          </h1>
          <p className="text-white/45 mt-3 font-sans text-sm max-w-3xl leading-relaxed">
            Default development mandate for new mandates. Each mandate can override individual
            sections; where a mandate leaves a section unset, these account defaults apply.
            Applies when mandate section is unset — scope context only, does not change Nuclius Index
            scoring.
          </p>
        </header>

        {success && (
          <Alert className="border-index-cleared/40 bg-green-950/40 text-index-cleared">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {reassessedScripts != null && reassessedScripts > 0
                ? `Mandate defaults saved — ${reassessedScripts} script${reassessedScripts === 1 ? '' : 's'} reassessed.`
                : 'Mandate defaults saved.'}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {DEVELOPMENT_MANDATE_SECTIONS.map((section) => (
            <section key={section.key} className="ni-panel p-6 space-y-4">
              <div>
                <h2 className="text-sm font-mono uppercase tracking-wider text-white/70">
                  {section.title}
                </h2>
                <p className="text-sm text-white/45 font-sans mt-1">{section.description}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {section.options.map((option) => {
                  const selected = mandate[section.key].includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      onClick={() => toggleOption(section.key, option.value)}
                      className={cn(
                        'rounded-sm border px-3 py-2 text-left text-sm font-sans transition-colors',
                        option.disabled && 'cursor-not-allowed opacity-40',
                        selected
                          ? 'border-white/60 bg-white/5 text-white'
                          : 'border-[#3a3a3a] text-white/65 hover:border-white/30 hover:text-white',
                      )}
                    >
                      <span className="block">{option.label}</span>
                      {option.description && (
                        <span className="block text-xs text-white/40 mt-0.5">{option.description}</span>
                      )}
                      {option.disabled && (
                        <span className="block text-[10px] uppercase tracking-wider text-white/30 mt-1">
                          Coming soon
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          {isAdmin && (
            <section className="ni-panel p-6 space-y-4 border border-amber-500/20">
              <div>
                <h2 className="text-sm font-mono uppercase tracking-wider text-amber-200/90">
                  Admin access
                </h2>
                <p className="mt-2 text-sm font-sans text-white/50">
                  Grant or revoke admin access for another user. Admins can view analysis
                  instrumentation trends and manage admin roles.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
                <label className="block">
                  <span className="text-xs font-mono text-white/45 uppercase tracking-wider">
                    User email
                  </span>
                  <input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="mt-2 w-full rounded-md border border-[#2a2a2a] bg-[#0d0d0d] px-3 py-2 text-sm text-white"
                    placeholder="user@example.com"
                  />
                  <ul className="mt-2 space-y-1 text-sm font-sans text-white/70">
                    {adminEmails.length ? (
                      adminEmails.map((email) => <li key={email}>{email}</li>)
                    ) : (
                      <li className="text-white/40">No admins listed</li>
                    )}
                  </ul>
                </label>
                <label className="block">
                  <span className="text-xs font-mono text-white/45 uppercase tracking-wider">
                    Access
                  </span>
                  <select
                    value={grantAdmin ? 'grant' : 'revoke'}
                    onChange={(e) => setGrantAdmin(e.target.value === 'grant')}
                    className="mt-2 w-full rounded-md border border-[#2a2a2a] bg-[#0d0d0d] px-3 py-2 text-sm text-white"
                  >
                    <option value="grant">Grant admin</option>
                    <option value="revoke">Revoke admin</option>
                  </select>
                </label>
                <Button
                  type="button"
                  variant="indexPrimary"
                  disabled={adminSaving || !adminEmail.trim()}
                  onClick={() => {
                    void (async () => {
                      setAdminSaving(true);
                      setAdminNotice(null);
                      try {
                        await setUserAdminStatus(adminEmail.trim(), grantAdmin);
                        setAdminNotice(
                          grantAdmin
                            ? `Admin access granted to ${adminEmail.trim()}.`
                            : `Admin access revoked for ${adminEmail.trim()}.`,
                        );
                        setAdminEmail('');
                        await refreshRoles();
                      } catch (err: unknown) {
                        setAdminNotice(
                          err instanceof Error ? err.message : 'Failed to update admin access',
                        );
                      } finally {
                        setAdminSaving(false);
                      }
                    })();
                  }}
                >
                  {adminSaving ? 'Saving…' : 'Update admin'}
                </Button>
              </div>
              {adminNotice && <p className="text-sm text-white/70 font-sans">{adminNotice}</p>}
            </section>
          )}

          {isAdmin && (
            <section className="ni-panel p-6 space-y-4 border border-sky-500/20">
              <div>
                <h2 className="text-sm font-mono uppercase tracking-wider text-sky-200/90">
                  Reviewer access
                </h2>
                <p className="mt-2 text-sm font-sans text-white/50">
                  Grant or revoke the script-label reviewer role. Reviewer-only accounts can open
                  /reviewer and nothing else.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
                <label className="block">
                  <span className="text-xs font-mono text-white/45 uppercase tracking-wider">
                    User email
                  </span>
                  <input
                    type="email"
                    value={reviewerEmail}
                    onChange={(e) => setReviewerEmail(e.target.value)}
                    className="mt-2 w-full rounded-md border border-[#2a2a2a] bg-[#0d0d0d] px-3 py-2 text-sm text-white"
                    placeholder="reviewer@example.com"
                  />
                  <ul className="mt-2 space-y-1 text-sm font-sans text-white/70">
                    {reviewerEmails.length ? (
                      reviewerEmails.map((email) => <li key={email}>{email}</li>)
                    ) : (
                      <li className="text-white/40">No reviewers listed</li>
                    )}
                  </ul>
                </label>
                <label className="block">
                  <span className="text-xs font-mono text-white/45 uppercase tracking-wider">
                    Access
                  </span>
                  <select
                    value={grantReviewer ? 'grant' : 'revoke'}
                    onChange={(e) => setGrantReviewer(e.target.value === 'grant')}
                    className="mt-2 w-full rounded-md border border-[#2a2a2a] bg-[#0d0d0d] px-3 py-2 text-sm text-white"
                  >
                    <option value="grant">Grant reviewer</option>
                    <option value="revoke">Revoke reviewer</option>
                  </select>
                </label>
                <Button
                  type="button"
                  variant="indexPrimary"
                  disabled={reviewerSaving || !reviewerEmail.trim()}
                  onClick={() => {
                    void (async () => {
                      setReviewerSaving(true);
                      setReviewerNotice(null);
                      try {
                        await setUserReviewerStatus(reviewerEmail.trim(), grantReviewer);
                        setReviewerNotice(
                          grantReviewer
                            ? `Reviewer access granted to ${reviewerEmail.trim()}.`
                            : `Reviewer access revoked for ${reviewerEmail.trim()}.`,
                        );
                        setReviewerEmail('');
                        await refreshRoles();
                      } catch (err: unknown) {
                        setReviewerNotice(
                          err instanceof Error ? err.message : 'Failed to update reviewer access',
                        );
                      } finally {
                        setReviewerSaving(false);
                      }
                    })();
                  }}
                >
                  {reviewerSaving ? 'Saving…' : 'Update reviewer'}
                </Button>
              </div>
              {reviewerNotice && <p className="text-sm text-white/70 font-sans">{reviewerNotice}</p>}
            </section>
          )}

          <div className="ni-panel p-6 space-y-3">
            <h2 className="text-sm font-mono uppercase tracking-wider text-white/70">
              Privacy &amp; discretion
            </h2>
            <p className="text-sm font-sans text-white/50">
              Your work and personal details are never stored. Screenplays are processed solely to
              generate an Nuclius Index report and then discarded.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="indexOutline"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="indexPrimary" disabled={saving}>
              {saving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save defaults
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </IndexWorkspaceShell>
  );
};

export default UserPreferencesPage;
